// TES-1128 — has a newer admin been deployed than the one this tab loaded?
//
// Twice on 2026-10-06 a tab opened before a deploy broke for staff (TES-1122,
// TES-1019). The tab now compares the build id baked into its own bundle with
// /version.json, which the same build writes next to index.html. The admin
// container's Caddy serves everything outside /assets with
// `Cache-Control: no-store`, and the check also fetches with
// `cache: "no-store"`, so the answer is always the deployed build's.
//
// Why this source and not another:
//   - the API's /health sha changes on every deploy, also when local-deploy
//     only retagged an unchanged admin image, and it names the API's commit,
//     not the bundle the tab runs;
//   - the hashed index-*.js name in index.html would need HTML parsing and
//     misses a change that only touches another chunk or the CSS.
//
// Pure on purpose: no React, no path aliases. The unit tests in
// `apps/admin/test` run it with plain `node --test`.

/** The id of a dev server's bundle: there is no deployed version to compare. */
export const DEV_BUILD_ID = "dev";

/**
 * True only when the served version names a different build than the
 * loaded one. Anything unreadable (an older deploy without the file, which
 * the SPA fallback answers with index.html, a network error, a malformed
 * body) is "no news", never a banner.
 */
export function isNewVersionAvailable(
  loadedBuildId: string,
  served: unknown,
): boolean {
  if (!loadedBuildId || loadedBuildId === DEV_BUILD_ID) return false;
  if (typeof served !== "object" || served === null) return false;
  const buildId = (served as { buildId?: unknown }).buildId;
  if (typeof buildId !== "string" || buildId.trim() === "") return false;
  return buildId !== loadedBuildId;
}

/** How often an idle tab asks, besides on focus and on a route change. */
export const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
/** Focus and route changes come in bursts; one ask per this window. */
export const VERSION_CHECK_MIN_GAP_MS = 30 * 1000;

/** Whether a check triggered at `now` should actually fetch. */
export function shouldCheckVersion(
  lastCheckedAt: number | null,
  now: number,
): boolean {
  return (
    lastCheckedAt === null || now - lastCheckedAt >= VERSION_CHECK_MIN_GAP_MS
  );
}

// The banner's staff copy (ours, pending Emelie's review).
export const NEW_VERSION_TEXT =
  "Det finns en ny version av admin. Ladda om sidan för att få den senaste versionen.";
export const NEW_VERSION_RELOAD_LABEL = "Ladda om";
